import React, { useState, useRef, useEffect } from 'react';
import { IoCheckmark } from 'react-icons/io5';
import { Flex } from 'lib/components/styles/layout';
import styled from 'styled-components/macro';

interface InlineTextEditProps {
  value: string;
  onSave: (newValue: string) => void;
  viewComponent: React.ElementType;
  disabled?: boolean;
}

const StyledInput = styled.input<{ inputWidth: number }>`
  border: none;
  outline: none;
  padding: 0;
  margin: 0;
  background: transparent;
  width: ${props => props.inputWidth}px;
  text-decoration: underline;

  &:focus {
    outline: none;
  }
`;

const HiddenSpan = styled.span`
  position: absolute;
  top: -9999px;
  left: 0;
  white-space: pre;
  visibility: hidden;
  padding: 0;
  margin: 0;
`;

export const InlineTextEdit = ({
  value,
  onSave,
  viewComponent: ViewComponent,
  disabled,
}: InlineTextEditProps) => {
  const [isEditing, setIsEditing] = useState(false);
  const [inputValue, setInputValue] = useState(value);
  const [inputWidth, setInputWidth] = useState(1);

  const inputRef = useRef<HTMLInputElement>(null);
  const spanRef = useRef<HTMLSpanElement>(null);

  useEffect(() => {
    setInputValue(value);
  }, [value]);

  useEffect(() => {
    if (isEditing) {
      inputRef.current?.focus();
      if (spanRef.current) {
        setInputWidth(spanRef.current.offsetWidth + 2);
      }
    }
  }, [isEditing, inputValue]);

  const handleSave = () => {
    const newValue = inputValue.trim();
    if (newValue && newValue !== value) {
      onSave(newValue);
    } else {
      setInputValue(value);
    }
    setIsEditing(false);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      handleSave();
    } else if (e.key === 'Escape') {
      setInputValue(value);
      setIsEditing(false);
    }
  };

  if (isEditing) {
    return (
      <Flex
        flexDirection='row'
        gap='6px'
        alignItems='center'
        style={{ position: 'relative' }}
      >
        <ViewComponent
          as={StyledInput}
          type='text'
          autoFocus
          ref={inputRef}
          value={inputValue}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
            setInputValue(e.currentTarget.value)
          }
          onBlur={handleSave}
          onKeyDown={handleKeyDown}
          inputWidth={inputWidth}
        />
        <ViewComponent as={HiddenSpan} ref={spanRef}>
          {inputValue || ' '}
        </ViewComponent>
        <IoCheckmark
          size={20}
          onMouseDown={(e: React.MouseEvent<SVGElement>) => {
            e.preventDefault();
            handleSave();
          }}
          style={{ cursor: 'pointer', flexShrink: 0 }}
        />
      </Flex>
    );
  }

  return (
    <ViewComponent
      onClick={() => !disabled && setIsEditing(true)}
      title={inputValue}
    >
      {inputValue}
    </ViewComponent>
  );
};
